import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Send, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  createDraft, patchDraft, getContract, transition, emptyRateCard,
} from "../../services/b2bContracts";

/**
 * ContractDraftDialog — Iteration 65
 *
 * Create or edit a `draft` contract proposal. Submitting is the "Send
 * for approval" path that transitions draft → submitted.
 *
 * Why a single dialog (not a page): drafts are small forms; keeping
 * them inside the dialog lets managers go from "I should propose X"
 * to "submitted" in one screen without losing the list context.
 */
export default function ContractDraftDialog({ open, contractId, onClose }) {
  const [doc, setDoc] = useState(blank());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!contractId) { setDoc(blank()); return; }
    (async () => {
      setLoading(true);
      try {
        const data = await getContract(contractId);
        setDoc(hydrate(data));
      } catch {
        toast.error("Could not load draft");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, contractId]);

  const set = (key) => (val) => setDoc((d) => ({ ...d, [key]: val }));
  const setNum = (key) => (e) => setDoc((d) => ({ ...d, [key]: Number(e.target.value || 0) }));

  const addRateCard = () => setDoc((d) => ({ ...d, rate_cards: [...d.rate_cards, emptyRateCard()] }));
  const updateRC = (i, patch) => setDoc((d) => {
    const next = d.rate_cards.slice();
    next[i] = { ...next[i], ...patch };
    return { ...d, rate_cards: next };
  });
  const removeRC = (i) => setDoc((d) => ({ ...d, rate_cards: d.rate_cards.filter((_, j) => j !== i) }));

  const addSlab = (i) => updateRC(i, { slabs: [...doc.rate_cards[i].slabs, { up_to_kg: 0, base_inr: 0, per_extra_kg_inr: 0 }] });
  const updateSlab = (i, j, key, val) => updateRC(i, {
    slabs: doc.rate_cards[i].slabs.map((s, k) => (k === j ? { ...s, [key]: Number(val) } : s)),
  });
  const removeSlab = (i, j) => updateRC(i, { slabs: doc.rate_cards[i].slabs.filter((_, k) => k !== j) });

  const persist = async ({ submit = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        ...doc,
        user_ids: typeof doc.user_ids === "string"
          ? doc.user_ids.split(",").map((s) => s.trim()).filter(Boolean)
          : (doc.user_ids || []),
      };
      let saved;
      if (contractId) saved = await patchDraft(contractId, payload);
      else            saved = await createDraft(payload);
      toast.success(contractId ? "Draft updated" : "Draft created");
      if (submit) {
        await transition(saved.id, "submit");
        toast.success("Submitted for approval");
      }
      onClose?.(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.validation_errors) {
        toast.error(detail.validation_errors.join("\n"));
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not save");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-draft-dialog">
        <DialogHeader>
          <DialogTitle>{contractId ? "Edit proposal" : "New B2B contract proposal"}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" /></div>
        ) : (
          <div className="space-y-4 pt-1">
            <Section title="Company & GST">
              <Two>
                <Field label="Company name *">
                  <Input value={doc.company_name} onChange={(e) => set("company_name")(e.target.value)} placeholder="Acme Corp" data-testid="draft-company-name" />
                </Field>
                <Field label="Legal entity">
                  <Input value={doc.legal_name} onChange={(e) => set("legal_name")(e.target.value)} placeholder="Acme Pvt Ltd" />
                </Field>
              </Two>
              <Two>
                <Field label="GSTIN">
                  <Input value={doc.gstin} onChange={(e) => set("gstin")(e.target.value.toUpperCase())} placeholder="29ABCDE1234F1Z5" />
                </Field>
                <Field label="Billing address">
                  <Input value={doc.billing_address} onChange={(e) => set("billing_address")(e.target.value)} placeholder="100 MG Road, Bangalore" />
                </Field>
              </Two>
              <Field label="B2B user_ids (comma-separated)" hint="Get these from the user's profile or the B2B account list.">
                <Input
                  value={Array.isArray(doc.user_ids) ? doc.user_ids.join(", ") : doc.user_ids}
                  onChange={(e) => set("user_ids")(e.target.value)}
                  placeholder="6a15ccfd9db77b8b0e546d35, …"
                  data-testid="draft-user-ids"
                />
              </Field>
            </Section>

            <Section title="Scope & validity">
              <Two>
                <Field label="Origin city">
                  <Input value={doc.origin_city || ""} onChange={(e) => set("origin_city")(e.target.value)} placeholder="Bangalore" />
                </Field>
                <Field label="Origin hub ID">
                  <Input value={doc.origin_hub_id || ""} onChange={(e) => set("origin_hub_id")(e.target.value)} placeholder="BLR-MAIN" />
                </Field>
              </Two>
              <Two>
                <Field label="Start date *">
                  <Input type="date" value={dateOnly(doc.start_date)} onChange={(e) => set("start_date")(e.target.value)} data-testid="draft-start-date" />
                </Field>
                <Field label="End date *">
                  <Input type="date" value={dateOnly(doc.end_date)} onChange={(e) => set("end_date")(e.target.value)} data-testid="draft-end-date" />
                </Field>
              </Two>
              <Field label="Min monthly volume">
                <Input type="number" min={0} value={doc.min_monthly_volume} onChange={setNum("min_monthly_volume")} />
              </Field>
            </Section>

            <Section title="Rate cards *" right={
              <Button size="sm" variant="outline" onClick={addRateCard} className="rounded-lg text-xs" data-testid="draft-add-rc">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add rate card
              </Button>
            }>
              {doc.rate_cards.length === 0 && (
                <p className="text-xs text-slate-500">Add a rate card to set weight-slab pricing.</p>
              )}
              {doc.rate_cards.map((rc, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3 mb-3" data-testid={`draft-rc-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Select value={rc.scope} onValueChange={(v) => updateRC(i, { scope: v })}>
                      <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="national">National</SelectItem>
                        <SelectItem value="regional">Regional</SelectItem>
                        <SelectItem value="local">Local</SelectItem>
                        <SelectItem value="hub_pair">Hub pair</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={rc.service_type} onValueChange={(v) => updateRC(i, { service_type: v })}>
                      <SelectTrigger className="h-8 text-xs w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="express">Express</SelectItem>
                      </SelectContent>
                    </Select>
                    {rc.scope === "hub_pair" && (
                      <>
                        <Input className="h-8 text-xs w-32" placeholder="origin hub" value={rc.origin_hub_id || ""} onChange={(e) => updateRC(i, { origin_hub_id: e.target.value })} />
                        <Input className="h-8 text-xs w-32" placeholder="dest hub" value={rc.destination_hub_id || ""} onChange={(e) => updateRC(i, { destination_hub_id: e.target.value })} />
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeRC(i)} className="h-8 ml-auto text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider grid grid-cols-12 gap-2 px-1 mb-1">
                    <div className="col-span-4">Up to (kg)</div>
                    <div className="col-span-4">Base (INR)</div>
                    <div className="col-span-3">Per extra kg</div>
                    <div className="col-span-1"></div>
                  </div>
                  {rc.slabs.map((s, j) => (
                    <div key={j} className="grid grid-cols-12 gap-2 mb-1" data-testid={`draft-rc-${i}-slab-${j}`}>
                      <Input className="col-span-4 h-8 text-xs" type="number" min={0} value={s.up_to_kg} onChange={(e) => updateSlab(i, j, "up_to_kg", e.target.value)} />
                      <Input className="col-span-4 h-8 text-xs" type="number" min={0} value={s.base_inr} onChange={(e) => updateSlab(i, j, "base_inr", e.target.value)} />
                      <Input className="col-span-3 h-8 text-xs" type="number" min={0} value={s.per_extra_kg_inr} onChange={(e) => updateSlab(i, j, "per_extra_kg_inr", e.target.value)} />
                      <Button size="sm" variant="ghost" onClick={() => removeSlab(i, j)} className="col-span-1 h-8 text-rose-600 p-0"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => addSlab(i)} className="h-7 text-[11px] mt-1">+ slab</Button>
                </div>
              ))}
            </Section>

            <Section title="Ancillary charges">
              <Two>
                <Field label="Pickup (INR)"><Input type="number" min={0} value={doc.pickup_charge} onChange={setNum("pickup_charge")} /></Field>
                <Field label="Packing (INR)"><Input type="number" min={0} value={doc.packing_charge} onChange={setNum("packing_charge")} /></Field>
              </Two>
              <Two>
                <Field label="COD flat (INR)"><Input type="number" min={0} value={doc.cod_fee_flat} onChange={setNum("cod_fee_flat")} /></Field>
                <Field label="COD % of declared value"><Input type="number" min={0} max={100} step={0.1} value={doc.cod_fee_pct} onChange={setNum("cod_fee_pct")} /></Field>
              </Two>
              <Two>
                <Field label="RTO fee %"><Input type="number" min={0} max={100} step={0.1} value={doc.rto_fee_pct} onChange={setNum("rto_fee_pct")} /></Field>
                <Field label="Fuel surcharge %"><Input type="number" min={0} max={100} step={0.1} value={doc.fuel_surcharge_pct} onChange={setNum("fuel_surcharge_pct")} /></Field>
              </Two>
              <Two>
                <Field label="GST %"><Input type="number" min={0} max={100} step={0.1} value={doc.gst_pct} onChange={setNum("gst_pct")} /></Field>
                <Field label="Volume discount %"><Input type="number" min={0} max={100} step={0.1} value={doc.volume_discount_pct} onChange={setNum("volume_discount_pct")} /></Field>
              </Two>
              <Field label="Special discount %"><Input type="number" min={0} max={100} step={0.1} value={doc.special_discount_pct} onChange={setNum("special_discount_pct")} /></Field>
            </Section>

            <Section title="Notes">
              <Textarea value={doc.notes || ""} onChange={(e) => set("notes")(e.target.value)} placeholder="Special terms, SLA commitments, peak surcharges, etc." rows={3} />
            </Section>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
              <Button onClick={() => persist()} disabled={saving} className="bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid="draft-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save draft</>}
              </Button>
              <Button onClick={() => persist({ submit: true })} disabled={saving} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white" data-testid="draft-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Save & submit</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function blank() {
  return {
    company_name: "", legal_name: "", gstin: "", billing_address: "",
    user_ids: [],
    origin_city: "", origin_hub_id: "",
    start_date: "", end_date: "",
    min_monthly_volume: 0,
    rate_cards: [emptyRateCard()],
    pickup_charge: 0, packing_charge: 0,
    cod_fee_flat: 0, cod_fee_pct: 0, rto_fee_pct: 0, fuel_surcharge_pct: 0, gst_pct: 18,
    volume_discount_pct: 0, special_discount_pct: 0,
    notes: "",
  };
}
function hydrate(d) { return { ...blank(), ...d }; }
function dateOnly(iso) { return iso ? String(iso).slice(0, 10) : ""; }

function Section({ title, right, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</h4>
        {right}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Two({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{children}</div>; }
function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      {children}
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}
